<template>
  <div class="brand-nav-tree-mappings">
    <div
      v-if="showBenefitsSection"
      class="brand-nav-tree-section container-bg-white mt-3"
    >
      <div class="brand-nav-tree-section-header">
        <div class="brand-nav-tree-section-heading">
          <h3 class="brand-nav-tree-section-title">{{ $t('flexben.brand.nav_tree.benefits_title') }}</h3>
          <p class="brand-nav-tree-section-hint">{{ $t('flexben.brand.nav_tree.section_hint') }}</p>
        </div>
        <button
          type="button"
          class="btn btn-success mapping-add-btn"
          @click="addNewRow('benefit')"
        >
          <font-awesome-icon icon="plus" />
          <span class="mapping-add-btn-text">{{ $t('flexben.brand.nav_tree.add_mapping') }}</span>
        </button>
      </div>

      <div
        v-if="benefitRows.length === 0"
        class="mapping-empty-state"
      >
        <template v-if="benefitCategories.length === 0">
          <p class="mapping-empty-state-text mb-0">{{ $t('flexben.brand.nav_tree.no_categories') }}</p>
        </template>
        <template v-else>
          <font-awesome-icon icon="map" class="mapping-empty-state-icon" />
          <p class="mapping-empty-state-title mb-1">{{ $t('flexben.brand.nav_tree.empty_title') }}</p>
          <p class="mapping-empty-state-subtitle mb-0">{{ $t('flexben.brand.nav_tree.empty_subtitle') }}</p>
        </template>
      </div>

      <div
        v-if="benefitRows.length > 0"
        class="mapping-cards-list"
      >
        <div
          v-for="row in benefitRows"
          :key="row.key"
          class="mapping-card"
          :class="{
            'mapping-card--existing': row.isExisting,
            'mapping-card--loading': isRowBusy(row),
            'mapping-card--has-error': row.serverError
          }"
        >
          <div class="mapping-card-header">
            <span
              class="mapping-badge"
              :class="row.isExisting ? 'mapping-badge--existing' : 'mapping-badge--new'"
            >
              {{ row.isExisting ? $t('flexben.brand.nav_tree.badge_existing') : $t('flexben.brand.nav_tree.badge_new') }}
            </span>
            <div class="mapping-card-actions">
              <button
                v-if="!row.isExisting"
                type="button"
                class="mapping-save-btn"
                :disabled="!isRowSavable('benefit', row)"
                @click="saveRow('benefit', row)"
              >
                <font-awesome-icon v-if="row.isSaving" icon="spinner" spin />
                <span v-else>{{ $t('flexben.brand.nav_tree.save_mapping') }}</span>
              </button>
              <button
                type="button"
                class="mapping-remove-btn"
                :disabled="isRowBusy(row)"
                :title="row.isExisting ? $t('flexben.general.btn.delete') : $t('flexben.brand.nav_tree.cancel')"
                @click="handleRowDelete('benefit', row)"
              >
                <font-awesome-icon v-if="row.isDeleting" icon="spinner" spin />
                <font-awesome-icon v-else icon="trash-alt" />
              </button>
            </div>
          </div>

          <div class="mapping-card-grid">
            <div class="mapping-field">
              <label class="mapping-field-label">{{ $t('flexben.brand.nav_tree.category') }}</label>
              <select
                class="form-control form-control-sm mapping-select"
                :class="{
                  'mapping-select--readonly': row.isExisting || isRowBusy(row)
                }"
                :disabled="row.isExisting || isRowBusy(row)"
                v-model="row.category_id"
                @change="onCategoryChange(row, benefitCategories)"
              >
                <option :value="null">-</option>
                <option
                  v-for="category in getAvailableCategories(row, benefitCategories, benefitRows)"
                  :key="'benefit-category-' + category.id"
                  :value="category.id"
                >
                  {{ category.name }}
                </option>
              </select>
            </div>

            <div class="mapping-field">
              <label class="mapping-field-label">{{ $t('flexben.brand.nav_tree.subcategory') }}</label>
              <select
                class="form-control form-control-sm mapping-select"
                :class="{
                  'mapping-select--readonly': row.isExisting || isRowBusy(row)
                }"
                :disabled="row.isExisting || isRowBusy(row) || !row.category_id"
                v-model="row.subcategory_id"
              >
                <option :value="null">{{ directCategoryLabel }}</option>
                <option
                  v-for="subcategory in getSubcategoriesForCategory(row.category_id, benefitCategories)"
                  :key="'benefit-subcategory-' + subcategory.id"
                  :value="subcategory.id"
                  :disabled="isSubcategoryDisabled(row, subcategory.id, benefitRows)"
                >
                  {{ subcategory.name }}
                </option>
              </select>
            </div>
          </div>

          <p v-if="row.serverError === 'save'" class="mapping-server-error mb-0">
            {{ $t('flexben.brand.nav_tree.save_error') }}
          </p>
          <p v-if="row.serverError === 'delete'" class="mapping-server-error mb-0">
            {{ $t('flexben.brand.nav_tree.delete_error') }}
          </p>
        </div>
      </div>
    </div>

    <div
      v-if="showOffersSection"
      class="brand-nav-tree-section container-bg-white mt-3"
    >
      <div class="brand-nav-tree-section-header">
        <div class="brand-nav-tree-section-heading">
          <h3 class="brand-nav-tree-section-title">{{ $t('flexben.brand.nav_tree.offers_title') }}</h3>
          <p class="brand-nav-tree-section-hint">{{ $t('flexben.brand.nav_tree.section_hint') }}</p>
        </div>
        <button
          type="button"
          class="btn btn-success mapping-add-btn"
          @click="addNewRow('offer')"
        >
          <font-awesome-icon icon="plus" />
          <span class="mapping-add-btn-text">{{ $t('flexben.brand.nav_tree.add_mapping') }}</span>
        </button>
      </div>

      <div
        v-if="offerRows.length === 0"
        class="mapping-empty-state"
      >
        <template v-if="offerCategories.length === 0">
          <p class="mapping-empty-state-text mb-0">{{ $t('flexben.brand.nav_tree.no_categories') }}</p>
        </template>
        <template v-else>
          <font-awesome-icon icon="map" class="mapping-empty-state-icon" />
          <p class="mapping-empty-state-title mb-1">{{ $t('flexben.brand.nav_tree.empty_title') }}</p>
          <p class="mapping-empty-state-subtitle mb-0">{{ $t('flexben.brand.nav_tree.empty_subtitle') }}</p>
        </template>
      </div>

      <div
        v-if="offerRows.length > 0"
        class="mapping-cards-list"
      >
        <div
          v-for="row in offerRows"
          :key="row.key"
          class="mapping-card"
          :class="{
            'mapping-card--existing': row.isExisting,
            'mapping-card--loading': isRowBusy(row),
            'mapping-card--has-error': row.serverError
          }"
        >
          <div class="mapping-card-header">
            <span
              class="mapping-badge"
              :class="row.isExisting ? 'mapping-badge--existing' : 'mapping-badge--new'"
            >
              {{ row.isExisting ? $t('flexben.brand.nav_tree.badge_existing') : $t('flexben.brand.nav_tree.badge_new') }}
            </span>
            <div class="mapping-card-actions">
              <button
                v-if="!row.isExisting"
                type="button"
                class="mapping-save-btn"
                :disabled="!isRowSavable('offer', row)"
                @click="saveRow('offer', row)"
              >
                <font-awesome-icon v-if="row.isSaving" icon="spinner" spin />
                <span v-else>{{ $t('flexben.brand.nav_tree.save_mapping') }}</span>
              </button>
              <button
                type="button"
                class="mapping-remove-btn"
                :disabled="isRowBusy(row)"
                :title="row.isExisting ? $t('flexben.general.btn.delete') : $t('flexben.brand.nav_tree.cancel')"
                @click="handleRowDelete('offer', row)"
              >
                <font-awesome-icon v-if="row.isDeleting" icon="spinner" spin />
                <font-awesome-icon v-else icon="trash-alt" />
              </button>
            </div>
          </div>

          <div class="mapping-card-grid">
            <div class="mapping-field">
              <label class="mapping-field-label">{{ $t('flexben.brand.nav_tree.category') }}</label>
              <select
                class="form-control form-control-sm mapping-select"
                :class="{
                  'mapping-select--readonly': row.isExisting || isRowBusy(row)
                }"
                :disabled="row.isExisting || isRowBusy(row)"
                v-model="row.category_id"
                @change="onCategoryChange(row, offerCategories)"
              >
                <option :value="null">-</option>
                <option
                  v-for="category in getAvailableCategories(row, offerCategories, offerRows)"
                  :key="'offer-category-' + category.id"
                  :value="category.id"
                >
                  {{ category.name }}
                </option>
              </select>
            </div>

            <div class="mapping-field">
              <label class="mapping-field-label">{{ $t('flexben.brand.nav_tree.subcategory') }}</label>
              <select
                class="form-control form-control-sm mapping-select"
                :class="{
                  'mapping-select--readonly': row.isExisting || isRowBusy(row)
                }"
                :disabled="row.isExisting || isRowBusy(row) || !row.category_id"
                v-model="row.subcategory_id"
              >
                <option :value="null">-</option>
                <option
                  v-for="subcategory in getSubcategoriesForCategory(row.category_id, offerCategories)"
                  :key="'offer-subcategory-' + subcategory.id"
                  :value="subcategory.id"
                  :disabled="isSubcategoryDisabled(row, subcategory.id, offerRows)"
                >
                  {{ subcategory.name }}
                </option>
              </select>
            </div>
          </div>

          <p v-if="row.serverError === 'save'" class="mapping-server-error mb-0">
            {{ $t('flexben.brand.nav_tree.save_error') }}
          </p>
          <p v-if="row.serverError === 'delete'" class="mapping-server-error mb-0">
            {{ $t('flexben.brand.nav_tree.delete_error') }}
          </p>
        </div>
      </div>
    </div>

    <div
      v-if="showBenefitsSection || showOffersSection"
      class="mapping-info-box"
    >
      <font-awesome-icon icon="info-circle" class="mapping-info-box-icon" />
      <span class="mapping-info-box-text">{{ $t('flexben.brand.nav_tree.mappings_saved_individually_info') }}</span>
    </div>
  </div>
</template>

<script>
import httpFlexbenService from '@/http/http-flexben';

let rowKeyCounter = 0;

function createRowKey() {
  rowKeyCounter += 1;
  return 'mapping-row-' + rowKeyCounter;
}

function normalizeSubcategoryId(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  return Number(value);
}

function mappingKey(categoryId, subcategoryId) {
  var normalizedSubcategoryId = subcategoryId === null || subcategoryId === undefined
    ? ''
    : subcategoryId;

  return String(categoryId) + '|' + String(normalizedSubcategoryId);
}

export default {
  name: 'BrandNavTreeMappings',

  props: {
    brandId: {
      type: Number,
      required: true,
    },
    navTreeData: {
      type: Object,
      required: true,
    },
  },

  data: function data() {
    return {
      benefitRows: [],
      offerRows: [],
      httpService: httpFlexbenService.brandService,
    };
  },

  computed: {
    showBenefitsSection: function showBenefitsSection() {
      return !!this.navTreeData;
    },

    showOffersSection: function showOffersSection() {
      return !!this.navTreeData;
    },

    benefitCategories: function benefitCategories() {
      if (this.navTreeData && Array.isArray(this.navTreeData.benefit_categories)) {
        return this.navTreeData.benefit_categories;
      }

      return [];
    },

    offerCategories: function offerCategories() {
      if (this.navTreeData && Array.isArray(this.navTreeData.offer_categories)) {
        return this.navTreeData.offer_categories;
      }

      return [];
    },

    directCategoryLabel: function directCategoryLabel() {
      return this.$t('flexben.brand.nav_tree.direct_category');
    },
  },

  watch: {
    navTreeData: {
      immediate: true,
      deep: true,
      handler: function handler() {
        this.initializeRows();
      },
    },
  },

  methods: {
    createEmptyRow: function createEmptyRow() {
      return {
        key: createRowKey(),
        id: null,
        category_id: null,
        subcategory_id: null,
        isExisting: false,
        isSaving: false,
        isDeleting: false,
        serverError: null,
      };
    },

    initializeRows: function initializeRows() {
      var benefitMappings = this.navTreeData && this.navTreeData.benefit_nav_tree_mappings
        ? this.navTreeData.benefit_nav_tree_mappings
        : [];

      var offerMappings = this.navTreeData && this.navTreeData.offer_nav_tree_mappings
        ? this.navTreeData.offer_nav_tree_mappings
        : [];

      var unsavedBenefitRows = this.benefitRows.filter(function filterUnsavedBenefitRows(row) {
        return !row.isExisting;
      });
      var unsavedOfferRows = this.offerRows.filter(function filterUnsavedOfferRows(row) {
        return !row.isExisting;
      });

      this.benefitRows = this.buildRowsFromMappings(benefitMappings, true).concat(unsavedBenefitRows);
      this.offerRows = this.buildRowsFromMappings(offerMappings, true).concat(unsavedOfferRows);

      this.emitPayload();
    },

    buildRowsFromMappings: function buildRowsFromMappings(mappings, isExisting) {
      return (mappings || []).map(function mapMappingToRow(mapping) {
        return {
          key: createRowKey(),
          id: mapping.id || null,
          category_id: mapping.category_id === undefined ? null : mapping.category_id,
          subcategory_id: normalizeSubcategoryId(mapping.subcategory_id),
          isExisting: isExisting,
          isSaving: false,
          isDeleting: false,
          serverError: null,
        };
      });
    },

    addNewRow: function addNewRow(type) {
      var row = this.createEmptyRow();

      if (type === 'benefit') {
        this.benefitRows = this.benefitRows.concat([row]);
        return;
      }

      this.offerRows = this.offerRows.concat([row]);
    },

    removeRow: function removeRow(type, rowKey) {
      if (type === 'benefit') {
        this.benefitRows = this.benefitRows.filter(function filterBenefitRow(row) {
          return row.key !== rowKey;
        });
        this.emitPayload();
        return;
      }

      this.offerRows = this.offerRows.filter(function filterOfferRow(row) {
        return row.key !== rowKey;
      });
      this.emitPayload();
    },

    isRowBusy: function isRowBusy(row) {
      return !!(row.isSaving || row.isDeleting);
    },

    isRowSavable: function isRowSavable(type, row) {
      if (row.isExisting || this.isRowBusy(row)) {
        return false;
      }

      if (!row.category_id) {
        return false;
      }

      if (type === 'offer' && row.subcategory_id === null) {
        return false;
      }

      return !this.isDuplicateRow(type, row);
    },

    isDuplicateRow: function isDuplicateRow(type, row) {
      var rows = type === 'benefit' ? this.benefitRows : this.offerRows;
      var currentKey = mappingKey(row.category_id, row.subcategory_id);

      return rows.some(function findDuplicate(existingRow) {
        if (existingRow.key === row.key || !existingRow.category_id) {
          return false;
        }

        return mappingKey(existingRow.category_id, existingRow.subcategory_id) === currentKey;
      });
    },

    saveRow: function saveRow(type, row) {
      var instance = this;

      if (!this.isRowSavable(type, row)) {
        return;
      }

      row.serverError = null;
      row.isSaving = true;

      this.httpService.createNavTreeMapping(this.brandId, {
        type: type,
        category_id: Number(row.category_id),
        subcategory_id: normalizeSubcategoryId(row.subcategory_id),
      }).then(function onSaveSuccess(response) {
        var mapping = response.data && response.data.mapping ? response.data.mapping : null;

        if (!mapping) {
          row.isSaving = false;
          row.serverError = 'save';
          return;
        }

        row.id = mapping.id;
        row.category_id = mapping.category_id;
        row.subcategory_id = normalizeSubcategoryId(mapping.subcategory_id);
        row.isExisting = true;
        row.isSaving = false;
        row.serverError = null;
        instance.emitPayload();
      }).catch(function onSaveError() {
        row.isSaving = false;
        row.serverError = 'save';
      });
    },

    handleRowDelete: function handleRowDelete(type, row) {
      var instance = this;

      if (!row.isExisting) {
        this.removeRow(type, row.key);
        return;
      }

      if (!row.id || this.isRowBusy(row)) {
        return;
      }

      row.serverError = null;
      row.isDeleting = true;

      this.httpService.deleteNavTreeMapping(this.brandId, row.id).then(function onDeleteSuccess() {
        instance.removeRow(type, row.key);
      }).catch(function onDeleteError() {
        row.isDeleting = false;
        row.serverError = 'delete';
      });
    },

    onCategoryChange: function onCategoryChange(row, categories) {
      row.serverError = null;

      var availableSubcategories = this.getSubcategoriesForCategory(row.category_id, categories);
      var subcategoryExists = availableSubcategories.some(function findSubcategory(subcategory) {
        return subcategory.id === row.subcategory_id;
      });

      if (row.subcategory_id !== null && !subcategoryExists) {
        row.subcategory_id = null;
      }
    },

    getSubcategoriesForCategory: function getSubcategoriesForCategory(categoryId, categories) {
      if (!categoryId) {
        return [];
      }

      var category = (categories || []).find(function findCategory(item) {
        return item.id === categoryId;
      });

      if (category && Array.isArray(category.subcategories)) {
        return category.subcategories;
      }

      return [];
    },

    getSelectedKeys: function getSelectedKeys(rows, excludeKey) {
      var ignoredKey = excludeKey || null;

      return (rows || [])
        .filter(function filterSelectedRows(row) {
          if (row.key === ignoredKey || !row.category_id) {
            return false;
          }

          return row.isExisting || row.category_id;
        })
        .map(function mapSelectedRow(row) {
          return mappingKey(row.category_id, row.subcategory_id);
        });
    },

    getAvailableCategories: function getAvailableCategories(row, categories, rows) {
      var selectedKeys = this.getSelectedKeys(rows, row.key);

      return (categories || []).filter(function filterAvailableCategory(category) {
        var directKey = mappingKey(category.id, null);
        var subcategories = Array.isArray(category.subcategories) ? category.subcategories : [];

        var hasAvailableSubcategory = subcategories.some(function findAvailableSubcategory(subcategory) {
          return selectedKeys.indexOf(mappingKey(category.id, subcategory.id)) === -1;
        });

        return selectedKeys.indexOf(directKey) === -1 || hasAvailableSubcategory;
      });
    },

    isSubcategoryDisabled: function isSubcategoryDisabled(row, subcategoryId, rows) {
      return this.getSelectedKeys(rows, row.key).indexOf(
        mappingKey(row.category_id, subcategoryId)
      ) !== -1;
    },

    serializeRows: function serializeRows(rows) {
      return (rows || [])
        .filter(function filterSerializableRow(row) {
          return row.isExisting && row.category_id;
        })
        .map(function mapSerializableRow(row) {
          return {
            id: row.id,
            category_id: Number(row.category_id),
            subcategory_id: normalizeSubcategoryId(row.subcategory_id),
          };
        });
    },

    emitPayload: function emitPayload() {
      this.$emit('change', {
        benefit_nav_tree_mappings: this.serializeRows(this.benefitRows),
        offer_nav_tree_mappings: this.serializeRows(this.offerRows),
      });
    },

    validate: function validate() {
      return {};
    },
  },
};
</script>

<style scoped lang="scss">
.brand-nav-tree-mappings {
  .brand-nav-tree-section {
    padding: 14px 16px;
  }

  .brand-nav-tree-section-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
  }

  .brand-nav-tree-section-heading {
    flex: 1;
    min-width: 0;
  }

  .brand-nav-tree-section-title {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 700;
    color: #101828;
  }

  .brand-nav-tree-section-hint {
    margin: 4px 0 0;
    font-size: 0.82rem;
    color: #667085;
    line-height: 1.4;
  }

  .mapping-add-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    padding: 6px 12px;
    font-size: 0.82rem;
    font-weight: 600;
    line-height: 1.2;
    white-space: nowrap;
  }

  .mapping-add-btn-text {
    margin-left: 6px;
  }

  .mapping-cards-list {
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    gap: 8px;
    margin-top: 2px;
  }

  .mapping-card {
    flex: 0 1 210px;
    width: 210px;
    max-width: 100%;
    background: #fff;
    border: 1px solid #e4e9f0;
    border-radius: 10px;
    padding: 8px 10px;
    margin-bottom: 0;
  }

  .mapping-card--has-error {
    border-color: #fecdca;
  }

  .mapping-card--existing {
    background: #fafbfc;
  }

  .mapping-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
    gap: 6px;
  }

  .mapping-card-actions {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
  }

  .mapping-save-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 52px;
    height: 24px;
    padding: 0 8px;
    border: 1px solid #abefc6;
    border-radius: 6px;
    background: #ecfdf3;
    color: #027a48;
    font-size: 0.68rem;
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;
  }

  .mapping-save-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  .mapping-card--loading {
    opacity: 0.75;
  }

  .mapping-server-error {
    margin-top: 6px;
    font-size: 0.68rem;
    color: #d92d20;
    line-height: 1.3;
  }

  .mapping-badge {
    display: inline-block;
    padding: 1px 6px;
    border-radius: 999px;
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    line-height: 1.4;
  }

  .mapping-badge--existing {
    background: #eef2f6;
    color: #475467;
    border: 1px solid #dde3ea;
  }

  .mapping-badge--new {
    background: #ecfdf3;
    color: #027a48;
    border: 1px solid #abefc6;
  }

  .mapping-remove-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 1px solid #f3d0d4;
    border-radius: 6px;
    background: #fff5f5;
    color: #d92d20;
    font-size: 0.72rem;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;
  }

  .mapping-remove-btn:hover {
    background: #fee4e2;
    border-color: #fecdca;
  }

  .mapping-remove-btn:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(217, 45, 32, 0.15);
  }

  .mapping-card-grid {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .mapping-field {
    width: 100%;
    margin-bottom: 0;
  }

  .mapping-field-label {
    display: block;
    margin-bottom: 2px;
    font-size: 0.68rem;
    font-weight: 600;
    color: #667085;
  }

  .mapping-select {
    border-radius: 6px;
    border-color: #d0d5dd;
    min-height: 30px;
    height: 30px;
    padding: 2px 8px;
    font-size: 0.78rem;
    line-height: 1.2;
  }

  .mapping-select--readonly:disabled,
  .mapping-select:disabled {
    background-color: #f8f9fb;
    color: #344054;
    opacity: 1;
    cursor: not-allowed;
    -webkit-text-fill-color: #344054;
  }

  .mapping-field-error {
    margin-top: 2px;
    font-size: 0.68rem;
    color: #d92d20;
    line-height: 1.3;
  }

  .mapping-card-errors {
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px solid #f2f4f7;
  }

  .mapping-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 18px 12px;
    margin-bottom: 8px;
    border: 1px dashed #d0d5dd;
    border-radius: 10px;
    background: #fafbfc;
  }

  .mapping-empty-state-icon {
    font-size: 1.25rem;
    color: #98a2b3;
    margin-bottom: 8px;
  }

  .mapping-empty-state-title {
    font-size: 0.88rem;
    font-weight: 600;
    color: #344054;
  }

  .mapping-empty-state-subtitle {
    font-size: 0.8rem;
    color: #667085;
  }

  .mapping-empty-state-text {
    font-size: 0.82rem;
    color: #667085;
  }

  .mapping-info-box {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-top: 12px;
    padding: 10px 12px;
    border-radius: 8px;
    background: #eff8ff;
    border: 1px solid #d1e9ff;
  }

  .mapping-info-box-icon {
    flex-shrink: 0;
    margin-top: 1px;
    font-size: 1rem;
    color: #1570ef;
  }

  .mapping-info-box-text {
    font-size: 0.82rem;
    line-height: 1.45;
    color: #175cd3;
  }
}

@media (max-width: 767px) {
  .brand-nav-tree-mappings {
    .mapping-cards-list {
      gap: 6px;
    }

    .mapping-card {
      flex: 1 1 calc(50% - 6px);
      width: auto;
      min-width: 160px;
    }

    .brand-nav-tree-section-header {
      flex-wrap: wrap;
    }

    .mapping-add-btn {
      width: 100%;
    }
  }
}

@media (max-width: 480px) {
  .brand-nav-tree-mappings {
    .mapping-card {
      flex: 1 1 100%;
      width: 100%;
    }
  }
}

@media (max-width: 575px) {
  .brand-nav-tree-mappings {
    .mapping-add-btn-text {
      display: none;
    }
  }
}
</style>
